import { UserProfile } from "@/types/org";
import { AgentProfile, AgentProfileRequest } from "@/types/profile";
import { THEME } from "@/enums/profile";
import { DEFAULT_DASHBOARD_LAYOUT } from "../dashboard";
import { GAIL_API } from "@/utils/common/constants/routes";
import { apiFetch, apiLive } from "@/lib/api";

/**
 * Fetches the agent profile from external API.
 * Uses cookie-based authentication (cookies shared via AUTH0_COOKIE_DOMAIN).
 */
export const GetAgentProfile = async (): Promise<AgentProfile | null> => {
  const response = await apiLive<AgentProfile>(`${GAIL_API}/.internal/agent`);

  return response;
};

/**
 * Updates the agent profile via external API.
 * Uses cookie-based authentication (cookies shared via AUTH0_COOKIE_DOMAIN).
 */
export const UpdateAgentProfile = async (
  profile: AgentProfileRequest
): Promise<AgentProfile | null> => {
  const response = await apiFetch<AgentProfile>(`${GAIL_API}/.internal/agent`, "PATCH", { body: profile });

  return response;
};

/**
 * Fetches the user profile from external API.
 * Uses cookie-based authentication (cookies shared via AUTH0_COOKIE_DOMAIN).
 * Note: userId is extracted from the cookie on the API side.
 */
export const GetUserProfile = async (userId: string): Promise<UserProfile> => {
  // Use silent: true because 404 is expected for new users without profiles yet
  // apiLive signature: (url, cacheKey, cacheTtl, fallback, silent)
  const response = await apiLive<UserProfile>(
    `${GAIL_API}/.internal/user_profile_settings/users/${userId}`,
    undefined, // cacheKey
    undefined, // cacheTtl
    undefined, // fallback
    true // silent - suppress 404 errors for new users
  );

  return {
    isDefaultProfile: !response,
    dashboardLayout:
      response?.dashboardLayout && response?.dashboardLayout?.length > 0
        ? response?.dashboardLayout
        : JSON.stringify(DEFAULT_DASHBOARD_LAYOUT),
    theme: response?.theme ?? THEME.LIGHT,
    language: response?.language,
    favorites: response?.favorites ?? {},
    columns: response?.columns ?? {},
  } satisfies UserProfile;
};

/**
 * Updates the user profile via external API.
 * Uses cookie-based authentication (cookies shared via AUTH0_COOKIE_DOMAIN).
 */
export const UpdateUserProfile = async (
  userId: string,
  profile: Partial<UserProfile>
): Promise<UserProfile | null> => {
  const response = await apiFetch<UserProfile>(`${GAIL_API}/.internal/user_profile_settings/users/${userId}`, "PATCH", { body: profile });

  return response;
};
